import { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ReferralWithMetadata, ReferralFormData } from '../../types/business';

interface AddReferralFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (referralData: ReferralFormData) => Promise<{ success: boolean; error?: string }>;
  onUpdate?: (
    referralId: string,
    updates: Partial<ReferralFormData>
  ) => Promise<{ success: boolean; error?: string }>;
  isSubmitting: boolean;
  isEdit?: boolean;
  editReferral?: ReferralWithMetadata;
  userType: 'business' | 'agent';
  userName: string;
  userEmail: string;
}

// Predefined specialty options for different categories
/*
const SPECIALTY_OPTIONS: Record<string, string[]> = {
  electrician: [
    'Electrical Wiring',
    'Panel Upgrades',
    'Lighting Installation',
    'Outlet Installation',
    'Electrical Inspection',
    'Home Automation',
    'Ceiling Fan Installation',
    'Generator Installation',
    'Emergency Electrical',
    'Code Compliance'
  ],
  plumber: [
    'Drain Cleaning',
    'Pipe Repair',
    'Water Heater Installation',
    'Leak Detection',
    'Toilet Repair',
    'Faucet Installation',
    'Sump Pump',
    'Water Line Repair',
    'Emergency Plumbing',
    'Bathroom Renovation'
  ],
  hvac: [
    'AC Installation',
    'Heating Repair',
    'Ductwork',
    'Maintenance',
    'Air Quality Testing',
    'Thermostat Installation',
    'Furnace Repair',
    'Heat Pump Service',
    'Energy Efficiency',
    'Emergency HVAC'
  ],
  handyman: [
    'General Repairs',
    'Drywall',
    'Painting',
    'Minor Electrical',
    'Carpentry',
    'Tile Work',
    'Flooring',
    'Cabinet Installation',
    'Window Repair',
    'Pressure Washing'
  ],
  other: [
    'Home Inspection',
    'Pest Control',
    'Landscaping',
    'Roofing',
    'Appliance Repair',
    'Security Systems',
    'Cleaning Services',
    'Moving Services',
    'Insurance',
    'Legal Services'
  ]
};
*/

const AddReferralForm: React.FC<AddReferralFormProps> = ({
  open,
  onClose,
  onSubmit,
  onUpdate,
  isSubmitting,
  isEdit = false,
  editReferral,
  userType,
  userName
}) => {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [solutionCategory, setSolutionCategory] = useState<string>('');
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [agentNotes, setAgentNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const nameRef = useRef<HTMLInputElement>(null);

  // Initialize form with edit data when in edit mode
  useEffect(() => {
    if (open && isEdit && editReferral) {
      setName(editReferral.name);
      setCompany(editReferral.company || '');
      setPhone(editReferral.phone || '');
      setEmail(editReferral.email || '');
      setWebsite(editReferral.website || '');
      setSolutionCategory(editReferral.solutionCategory); // camelCase
      setSpecialties(editReferral.specialties || []);
      setDescription(editReferral.description || '');
      setAgentNotes(editReferral.agentNotes || ''); // camelCase
    } else if (open && !isEdit) {
      // Reset form for add mode
      setName('');
      setCompany('');
      setPhone('');
      setEmail('');
      setWebsite('');
      setSolutionCategory('');
      setSpecialties([]);
      setDescription('');
      setAgentNotes('');
    }
    setError(null);
  }, [open, isEdit, editReferral]);
  
  const handleSubmit = async () => {
    if (!name.trim() || !solutionCategory) {
      setError('Name and solution category are required');
      return;
    }

    if (!email.trim() && !phone.trim()) {
      setError('Either email or phone number is required');
      return;
    }
    
    setError(null);
    
    const referralData: ReferralFormData = {
      name: name.trim(),
      company: company.trim() || undefined,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      website: website.trim() || undefined,
      solutionCategory: solutionCategory as any, // camelCase
      specialties: specialties,
      description: description.trim() || undefined,
      profileImage: undefined, // camelCase
      agentNotes: agentNotes.trim() || undefined // camelCase
    };

    let result;
    if (isEdit && onUpdate && editReferral) {
      // For edit mode, only send changed fields
      const updates: Partial<ReferralFormData> = {};
      
      if (name.trim() !== editReferral.name) updates.name = name.trim();
      if ((company.trim() || '') !== (editReferral.company || '')) updates.company = company.trim() || undefined;
      if ((phone.trim() || '') !== (editReferral.phone || '')) updates.phone = phone.trim() || undefined;
      if ((email.trim() || '') !== (editReferral.email || '')) updates.email = email.trim() || undefined;
      if ((website.trim() || '') !== (editReferral.website || '')) updates.website = website.trim() || undefined;
      if (solutionCategory !== editReferral.solutionCategory) updates.solutionCategory = solutionCategory as any; // camelCase
      if (JSON.stringify(specialties) !== JSON.stringify(editReferral.specialties)) updates.specialties = specialties;
      if ((description.trim() || '') !== (editReferral.description || '')) updates.description = description.trim() || undefined;
      if ((agentNotes.trim() || '') !== (editReferral.agentNotes || '')) updates.agentNotes = agentNotes.trim() || undefined; // camelCase
      
      result = await onUpdate(editReferral.id, updates);
    } else {
      // For add mode, send all data
      result = await onSubmit(referralData);
    }

    if (result.success) {
      handleClose();
    } else {
      setError(result.error || `Failed to ${isEdit ? 'update' : 'add'} referral`);
    }
  };
  
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !isSubmitting && name.trim() && solutionCategory) {
      handleSubmit();
    }
  };
  
  const handleClose = () => {
    setName('');
    setCompany('');
    setPhone('');
    setEmail('');
    setWebsite('');
    setSolutionCategory('');
    setSpecialties([]);
    setDescription('');
    setAgentNotes('');
    setError(null);
    onClose();
  };

  // const availableSpecialties = solutionCategory ? SPECIALTY_OPTIONS[solutionCategory] || [] : [];
  
  // Add focus management
  const handleDialogClose = (_event: {}, _reason: "backdropClick" | "escapeKeyDown") => {
    if (isSubmitting) return;
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
      // Remove custom z-index to prevent conflicts
      // sx={{ zIndex: 3001 }}
      // Add proper focus management
      disableRestoreFocus={false}
      disableAutoFocus={false}
      disableEnforceFocus={false}
      slotProps={{
        backdrop: {
          // Ensure backdrop doesn't interfere with focus
          invisible: false,
        },
        transition: {
          onEntered: () => {
            // Only focus if not in edit mode and dialog is actually open
            if (!isEdit && open && nameRef.current) {
              // Small delay to ensure dialog is fully rendered
              setTimeout(() => {
                nameRef.current?.focus();
              }, 100);
            }
          }
        }
      }}
    >
      <DialogTitle>
        {isEdit ? 'Edit Professional Referral' : 'Add Professional Referral'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}

          {/* User Info Display */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Adding as:</strong> {userName} ({userType === 'business' ? 'Business Admin' : 'Agent'})
              <br />
              {userType === 'business' 
                ? 'This referral will be available to all agents in your business.'
                : 'This referral will be available to your clients only.'
              }
            </Typography>
          </Alert>
          
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            Professional Information
          </Typography>
          
          <TextField
            ref={nameRef}
            fullWidth
            label="Name *"
            variant="filled"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            margin="dense"
            required
            disabled={isSubmitting}
            // Add proper ARIA attributes
            aria-required="true"
            aria-invalid={!name.trim() && error ? 'true' : 'false'}
          />

          <TextField
            fullWidth
            label="Company"
            variant="filled"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            onKeyDown={handleKeyDown}
            margin="dense"
            disabled={isSubmitting}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Phone"
              variant="filled"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={handleKeyDown}
              margin="dense"
              disabled={isSubmitting}
              type="tel"
            />
            <TextField
              fullWidth
              label="Email"
              variant="filled"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              margin="dense"
              disabled={isSubmitting}
            />
          </Box>

          <TextField
            fullWidth
            label="Website"
            variant="filled"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            onKeyDown={handleKeyDown}
            margin="dense"
            disabled={isSubmitting}
            placeholder="https://example.com"
            type="url"
          />

          <FormControl 
            variant="filled" 
            fullWidth 
            margin="dense" 
            required 
            disabled={isSubmitting}
            error={!solutionCategory && error ? true : false}
          >
            <InputLabel id="solution-category-label">
              Solution Category *
            </InputLabel>
            <Select
              labelId="solution-category-label"
              id="solution-category"
              value={solutionCategory}
              onChange={(e) => {
                setSolutionCategory(e.target.value as string);
                setSpecialties([]);
              }}
              aria-required="true"
              aria-invalid={!solutionCategory && error ? 'true' : 'false'}
            >
              <MenuItem value="">
                <em>Select a category</em>
              </MenuItem>
              <MenuItem value="electrician">⚡ Electrician</MenuItem>
              <MenuItem value="plumber">🔧 Plumber</MenuItem>
              <MenuItem value="hvac">🌡️ HVAC</MenuItem>
              <MenuItem value="handyman">🔨 Handyman</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Description"
            variant="filled"
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="dense"
            disabled={isSubmitting}
            placeholder="Brief description of services offered..."
          />

          <TextField
            fullWidth
            label="Agent Notes"
            variant="filled"
            multiline
            rows={2}
            value={agentNotes}
            onChange={(e) => setAgentNotes(e.target.value)}
            margin="dense"
            disabled={isSubmitting}
            placeholder="Internal notes about this referral..."
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            * Required fields
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !name.trim() || !solutionCategory}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting 
            ? `${isEdit ? 'Updating' : 'Adding'} Referral...`
            : `${isEdit ? 'Update' : 'Add'} Referral`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddReferralForm;